import { styled } from '@mui/material';

import {
  EFocusView,
  GeoMap,
  GeoMapWidgetPreview,
  type GeoMapProps,
  MapOverlay,
  Toolbar,
  useIsIdle,
  type WidgetConfigBase,
  type WidgetProps,
} from '#features';
import { EWidgetSection, Timezones } from '#shared';

import { Widget } from '../base';

type Settings = Pick<GeoMapProps, 'initialFocus'>;

const config: WidgetConfigBase<Settings> = {
  initialFocus: {
    type: 'select',
    label: 'Initial Focus',
    defaultValue: EFocusView.US_CONTINENTAL,
    options: Object.values(EFocusView).map(view => ({
      label: view,
      value: view as string,
    })),
  },
};

export interface WidgetGeoMapExternalProps extends Omit<
  Partial<GeoMapProps>,
  'contextId' | 'initialFocus'
> {
  /** Optional seed; Redux settings still control it */
  initialFocus?: GeoMapProps['initialFocus'];
  className?: string;
  showSettingsButton?: boolean;
  scale?: number;
  withTimerInside?: boolean;
  showToolbar?: boolean;
  showTimezones?: boolean;
  renderInCentralWidget?: boolean;
  mapPadding?: GeoMapProps['padding'];
  /** Render as preview with image instead of actual map component */
  preview?: boolean;
}

const Root = styled(Widget)(({ theme: { spacing } }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 'calc(100cqw / 20 * 9)',
  flexGrow: 1,
  borderRadius: spacing(2),
  overflow: 'hidden',
}));

export const WidgetGeoMap = ({
  className,
  showSettingsButton,
  instance,
  mapPadding,
  preview = false,
  ...defaults // external overrides like initialFocus
}: WidgetProps<Settings> & WidgetGeoMapExternalProps) => {
  const isIdle = useIsIdle();
  let showToolbar = false;
  let showTimezones = false;
  let withTimerInside = false;
  let renderInCentralWidget = false;
  if (instance?.sectionId === EWidgetSection.center) {
    showToolbar = true;
    showTimezones = true;
    withTimerInside = true;
    renderInCentralWidget = true;
  }

  // Central widget never uses standard toolbar (uses GeoMap Toolbar in preview mode)
  // Aside widgets (Focus View) always use standard toolbar
  const shouldRenderSettings = renderInCentralWidget
    ? false
    : showSettingsButton;

  return (
    <Root
      config={config}
      showSettingsButton={shouldRenderSettings}
      className={className}
      instance={instance}
    >
      {resolved => {
        const { initialFocus: settingsInitialFocus } = resolved as Settings;

        const initialFocus =
          settingsInitialFocus ??
          defaults.initialFocus ??
          EFocusView.US_CONTINENTAL;

        // Preview mode: render image instead of actual map
        if (preview) {
          const isCentralWidget = instance?.sectionId === EWidgetSection.center;

          return (
            <GeoMapWidgetPreview
              isCentralWidget={isCentralWidget}
              showToolbar={isCentralWidget}
            />
          );
        }

        return (
          <GeoMap
            initialFocus={initialFocus}
            className={className}
            padding={mapPadding}
            applyFocusZoomMultiplier={!renderInCentralWidget}
          >
            {showToolbar && <Toolbar />}
            {showTimezones && <Timezones isIdle={isIdle} />}
            <MapOverlay
              renderInCentralWidget={renderInCentralWidget}
              withTimerInside={withTimerInside}
              initialFocus={initialFocus}
            />
          </GeoMap>
        );
      }}
    </Root>
  );
};
WidgetGeoMap.config = config;
